import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { db, checkFirebaseConnection, safeFirestoreRead } from '../../shared/api/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { updateShortFormQuiz } from './ShortFormQuizService';
import { useAuth } from '../../shared/contexts/AuthContext';
import '../../styles/contents/shortFormQuiz/ShortFormQuizModifyPage.css';

// 헬퍼 함수들 (다른 파일과 동일)
const getCollectionName = (currentUser) => {
  return currentUser ? 'shortForm_Quiz_member' : 'shortForm_Quiz_guest';
};

const generateUserId = (currentUser) => {
  if (currentUser) return currentUser.uid;
  
  const guestIdKey = 'guest_shortform_id';
  const existingGuestId = localStorage.getItem(guestIdKey);
  if (existingGuestId) return existingGuestId;

  const timestamp = new Date().getTime();
  const randomStr = Math.random().toString(36).substring(2, 10);
  const newGuestId = `guest_${timestamp}_${randomStr}`;
  
  localStorage.setItem(guestIdKey, newGuestId);
  return newGuestId;
};

const getStorageKey = (currentUser, classId = null) => {
  const baseKey = currentUser ? `shortForm_savedQuizzes_${currentUser.uid}` : 'shortForm_savedQuizzes_guest';
  return classId ? `${baseKey}_${classId}` : baseKey;
};

const ShortFormQuizModifyPage = () => {
  const navigate = useNavigate();
  const { code } = useParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [isEditing, setIsEditing] = useState(true);
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // localStorage에서 클래스 정보 가져오기
  const getStoredClassData = () => {
    try {
      const classInfo = localStorage.getItem('classInfo');
      return classInfo ? JSON.parse(classInfo) : null;
    } catch (error) {
      return null;
    }
  };
  const classInfo = getStoredClassData();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let found = null;
        const userId = generateUserId(currentUser);
        
        if (!userId || !code) {
          setError('필수 정보가 없습니다.');
          return;
        }

        // 1. LocalStorage에서 먼저 확인
        try {
          const storageKey = getStorageKey(currentUser, classInfo?.classId);
          const savedQuizzesData = localStorage.getItem(storageKey);
          if (savedQuizzesData) {
            const parsedData = JSON.parse(savedQuizzesData);
            const savedQuizzes = Array.isArray(parsedData) ? parsedData : [];
            const matched = savedQuizzes.find(q => String(q.id) === String(code));
            if (matched) {
              found = {
                id: matched.id,
                title: matched.title,
                questions: matched.questions || [],
                meta: { 
                  grade: matched.grade, 
                  subject: matched.subject, 
                  mode: matched.mode, 
                  topic: matched.topic, 
                  school: matched.school 
                },
                createdAt: matched.createdAt,
                origin: 'localStorage'
              };
            }
          }
        } catch (parseError) {
          console.warn('localStorage 파싱 오류:', parseError);
        }

        // 2. Firebase에서도 확인 (새로운 구조)
        if (!found) {
          try {
            const connection = await checkFirebaseConnection();
            if (connection && connection.overall && db) {
              const collectionName = getCollectionName(currentUser);
              
              // 학급이 선택된 경우 학급별 문서 ID 생성
              let docId;
              if (classInfo?.classId) {
                docId = `${userId}_${classInfo.classId}`;
              } else {
                docId = userId;
              }
              
              const userDocRef = doc(db, collectionName, docId);
              const readResult = await safeFirestoreRead(userDocRef);
              
              if (readResult.success && readResult.doc.exists()) {
                const userData = readResult.doc.data();
                if (userData.퀴즈List && Array.isArray(userData.퀴즈List)) {
                  const matched = userData.퀴즈List.find(q => String(q.id) === String(code));
                  if (matched) {
                    found = {
                      id: matched.id,
                      title: matched.title,
                      questions: matched.questions || [],
                      meta: { 
                        grade: matched.grade, 
                        subject: matched.subject, 
                        mode: matched.mode, 
                        topic: matched.topic, 
                        school: matched.school 
                      },
                      createdAt: matched.createdAt,
                      origin: 'firebase'
                    };
                  }
                }
              }
            }
          } catch (firebaseError) {
            console.warn('Firebase 로드 실패:', firebaseError);
          }
        }

        if (!found) {
          setError('퀴즈를 찾을 수 없습니다.');
        } else {
          setQuiz(found);
          setTitle(found.title || '');
        }
      } catch (e) {
        console.error('퀴즈 로드 오류:', e);
        setError('퀴즈를 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code, currentUser, classInfo]);

  const getMetaText = () => {
    if (!quiz?.meta) return '';
    const parts = [];
    if (quiz.meta.school) parts.push(quiz.meta.school);
    if (quiz.meta.grade) parts.push(`${quiz.meta.grade}학년`);
    if (quiz.meta.subject) parts.push(quiz.meta.subject);
    if (quiz.meta.topic) parts.push(`주제: ${quiz.meta.topic}`);
    if (quiz.meta.mode) parts.push(quiz.meta.mode === 'ai' ? 'AI 생성' : '직접 생성');
    return parts.join(' · ');
  };

  if (loading) {
    return (
      <section className="content-start-section" style={{ minHeight: '100vh', paddingTop: '20px' }}>
        <div className="container" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 20px' }}>
          <div className="content-navigation">
            <button
              className="btn-ghost"
              onClick={() => navigate('/play/shortFormQuiz')}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--gray-300)' }}
            >
              <ArrowLeft size={16} />
              돌아가기
            </button>
          </div>
          <div className="tools-frame-content" style={{ textAlign: 'center' }}>불러오는 중...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="content-start-section" style={{ minHeight: '100vh', paddingTop: '20px' }}>
        <div className="container" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 20px' }}>
          <div className="content-navigation">
            <button
              className="btn-ghost"
              onClick={() => navigate('/play/shortFormQuiz')}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--gray-300)' }}
            >
              <ArrowLeft size={16} />
              돌아가기
            </button>
          </div>
          <div className="tools-frame-content" style={{ color: 'var(--danger, #dc3545)' }}>{error}</div>
        </div>
      </section>
    );
  }

  const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];

  const handleQuestionTextChange = (qIndex, value) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const nextQuestions = [...(prev.questions || [])];
      const target = { ...(nextQuestions[qIndex] || {}) };
      target.question = value;
      nextQuestions[qIndex] = target;
      return { ...prev, questions: nextQuestions };
    });
  };

  const handleOptionTextChange = (qIndex, optIndex, value) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const nextQuestions = [...(prev.questions || [])];
      const target = { ...(nextQuestions[qIndex] || {}) };
      const opts = Array.isArray(target.options) ? [...target.options] : [];
      while (opts.length < 4) opts.push(`선택지 ${String.fromCharCode(65 + opts.length)}`);
      opts[optIndex] = value;
      target.options = opts.slice(0, 4);
      nextQuestions[qIndex] = target;
      return { ...prev, questions: nextQuestions };
    });
  };

  const handleAnswerIndexChange = (qIndex, newIndex) => {
    setQuiz((prev) => {
      if (!prev) return prev;
      const nextQuestions = [...(prev.questions || [])];
      const target = { ...(nextQuestions[qIndex] || {}) };
      target.answerIndex = newIndex;
      nextQuestions[qIndex] = target;
      return { ...prev, questions: nextQuestions };
    });
  };

  const handleSave = async () => {
    if (!quiz?.id) return;
    if (!title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }
    setIsSaving(true);
    try {
      const res = await updateShortFormQuiz({
        id: String(quiz.id),
        title: title.trim(),
        questions: questions || [],
        meta: quiz?.meta || {},
        currentUser,
        classId: classInfo?.classId || null
      });
      if (!res?.success) throw new Error('failed');
      alert('저장되었습니다.');
      setQuiz((prev) => (prev ? { ...prev, title: title.trim() } : prev));
    } catch (e) {
      console.error('저장 오류:', e);
      alert('저장 중 오류가 발생했습니다. 다시 시도해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="content-start-section" style={{ minHeight: '100vh', paddingTop: '20px' }}>
      <div className="container" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto', padding: '0 20px' }}>
        <div className="content-navigation">
          <button
            className="btn-ghost"
            onClick={() => navigate('/play/shortFormQuiz')}
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', border: '1px solid var(--gray-300)' }}
          >
            <ArrowLeft size={16} />
            돌아가기
          </button>
        </div>

        <div className="tools-frame-content" style={{ 
          maxWidth: '1200px',
          width: '100%',
          margin: '0 auto',
          padding: '20px 24px 0'
        }}>
          <div style={{ display: 'grid', gap: 'var(--spacing-lg)' }}>
            <div>
              <h2 className="section-title" style={{ margin: 0 }}>퀴즈 수정</h2>
              <p className="section-description" style={{ margin: 0 }}>{getMetaText()}</p>
            </div>

            <div style={{ display: 'grid', gap: 'var(--spacing-sm)' }}>
              <label htmlFor="quizTitle" style={{ 
                margin: 0, 
                textAlign: 'left',
                fontSize: '20px',
                fontWeight: 700,
                color: 'var(--gray-900)',
                lineHeight: '1.3'
              }}>제목</label>
              <input
                id="quizTitle"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="예: 5학년 과학 상식 퀴즈 1"
                style={{ border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}
              />
            </div>

            {questions.length > 0 ? (
              <div style={{ display: 'grid', gap: 'var(--spacing-md)' }}>
                {/* 문제 편집 헤더 */}
                <h3 style={{ 
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--gray-900)',
                  lineHeight: '1.3'
                }}>문제 편집</h3>
                
                {/* 2열 그리드로 문제 배치 */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: 'var(--spacing-md)',
                  '@media (max-width: 900px)': {
                    gridTemplateColumns: '1fr'
                  }
                }}>
                  {questions.map((q, idx) => {
                    const opts = Array.isArray(q.options) ? q.options.slice(0, 4) : [];
                    while (opts.length < 4) opts.push(`선택지 ${String.fromCharCode(65 + opts.length)}`);
                    return (
                      <div key={idx} style={{ 
                        border: '1px solid var(--gray-200)', 
                        borderRadius: 'var(--radius-md)', 
                        padding: 'var(--spacing-md)', 
                        background: '#fff',
                        height: 'fit-content'
                      }}>
                        <div style={{ fontWeight: 700, marginBottom: 'var(--spacing-sm)' }}>문제 {idx + 1}</div>
                        <input
                          type="text"
                          value={q.question || ''}
                          onChange={(e) => handleQuestionTextChange(idx, e.target.value)}
                          placeholder="문제 내용을 입력하세요"
                          style={{ width: '100%', border: '1px solid var(--gray-300)', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}
                        />
                        <div style={{ display: 'grid', gap: 8 }}>
                          {opts.map((opt, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input
                                type="radio"
                                name={`answer_${idx}`}
                                checked={Number(q.answerIndex) === i}
                                onChange={() => handleAnswerIndexChange(idx, i)}
                              />
                              <input
                                type="text"
                                value={opt}
                                onChange={(e) => handleOptionTextChange(idx, i, e.target.value)}
                                placeholder={`선택지 ${String.fromCharCode(65 + i)}`}
                                style={{ flex: 1, border: '1px solid var(--gray-300)', borderRadius: 8, padding: '8px 10px' }}
                              />
                              {Number(q.answerIndex) === i && (
                                <span style={{ fontSize: 12, color: 'var(--primary)' }}>정답</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ color: 'var(--gray-500)' }}>문항이 없습니다.</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button className="btn-primary" onClick={handleSave} disabled={isSaving || !title.trim()}>
                {isSaving ? '저장 중...' : '저장하기'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShortFormQuizModifyPage;


